"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Droplets } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

export default function DustManagementPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const router = useRouter()

  useEffect(() => {
    const checkAuth = async () => {
      const sessionToken = localStorage.getItem("councilSessionToken")
      
      if (!sessionToken) {
        router.push("/council/login")
        return
      }

      try {
        const response = await fetch("/api/council/verify", {
          headers: { "Authorization": `Bearer ${sessionToken}` },
        })

        if (!response.ok) {
          localStorage.removeItem("councilSessionToken")
          router.push("/council/login")
          return
        }

        setIsAuthenticated(true)
        
        // Track page access
        try {
          await fetch("/api/council/activity", {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ 
              page: "/council/dust-management",
              action: "council_dust_management_view"
            }),
          })
        } catch (error) {
          console.error("Error tracking activity:", error)
        }
      } catch (error) {
        console.error("Auth verification error:", error)
        localStorage.removeItem("councilSessionToken")
        router.push("/council/login")
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [router])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b0861] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.push("/council/dashboard")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-[#5b0861]">PetAftercare</h1>
                <p className="text-sm text-gray-600">Dust Management Plan (DMP)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Introduction */}
        <Card className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-[#5b0861]">
              <Droplets className="h-6 w-6" />
              Dust Management Plan (DMP)
            </CardTitle>
            <CardDescription>
              A Dust Management Plan (DMP) must be devised, implemented, and managed by a suitably qualified person
            </CardDescription>
          </CardHeader>
        </Card>

        {/* 1. Site Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>1. Site Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              North Canterbury Pet Cremation will operate a small-scale pet cremation service located in Rangiora, Canterbury, on a rural property at the foothills of Mt Grey / Maukatere. The facility includes a fully enclosed shed where all cremation processing activities, including operation of the cremation machine, are undertaken.
            </p>
          </CardContent>
        </Card>

        {/* 2. Dust-Generating Activities */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>2. Dust-Generating Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-700">
                The following activities on-site have been assessed for potential dust generation:
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Activity</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Location</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Dust Risk</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Operation of cremulator</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Indoors (shed)</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Very Low</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Unit is fully enclosed and used infrequently</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Handling of ash remains</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Indoors (shed)</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Very Low</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Handled with care, in closed containers</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Movement of vehicles</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Driveway only</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Low</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">Gravel access maintained and watered as needed</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Mitigation Measures */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>3. Mitigation Measures</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <h4 className="font-semibold text-gray-900 mb-2">Enclosure and Containment</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                  <li>All dust-generating operations are conducted indoors in a fully enclosed structure.</li>
                  <li>The cremulator is a sealed unit, designed to minimise airborne particles.</li>
                  <li>Ash remains are transferred into sealed urns or containers immediately after processing.</li>
                </ul>
              </div>

              <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                <h4 className="font-semibold text-gray-900 mb-2">Housekeeping</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                  <li>The indoor workspace is regularly cleaned, with surfaces damp-wiped to prevent dust accumulation.</li>
                  <li>A HEPA-filter vacuum is used rather than dry sweeping.</li>
                  <li>Any ash spills are cleaned immediately and disposed of in sealed bins.</li>
                </ul>
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                <h4 className="font-semibold text-gray-900 mb-2">Ventilation Controls</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                  <li>Indoor areas are ventilated using filtered fans; no vents are directed toward neighbouring properties.</li>
                  <li>Doors are kept closed during cremulator operation.</li>
                </ul>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-500">
                <h4 className="font-semibold text-gray-900 mb-2">Vehicle Dust Control</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                  <li>The driveway is compacted gravel; it will be lightly sprayed with water during extended dry periods if dust becomes an issue.</li>
                  <li>Vehicle speeds on site are limited to 10 km/h to minimise dust agitation.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. Monitoring and Complaints */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>4. Monitoring and Complaints</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Staff will undertake weekly visual inspections of work areas and the site perimeter to ensure dust is not escaping the shed.</li>
              <li>A logbook will be maintained to record cleaning, inspections, and any maintenance of dust-related equipment.</li>
              <li>A complaints procedure is in place: any dust-related concerns from neighbours will be logged, investigated, and responded to within 48 hours.</li>
            </ul>
          </CardContent>
        </Card>

        {/* 5. Training and Responsibilities */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>5. Training and Responsibilities</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>All staff are trained in dust-safe procedures, including proper use of PPE (masks, gloves), cremulator operation, and cleaning protocols.</li>
              <li>The business owners (Ray and Molly Bachert) are the designated person responsible for DMP oversight.</li>
            </ul>
          </CardContent>
        </Card>

        {/* 6. Review and Update */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>6. Review and Update</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              This Dust Management Plan will be reviewed annually or when operations change, and updated if required by council feedback, health & safety audits, or iwi consultation.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
